import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star, Quote, ChevronLeft, ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import { testimonialsData, personalInfo } from '../data/mock';

const TestimonialsPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Auto-scroll carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonialsData.length - 1 : prev - 1));
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    setIsAutoPlaying(false);
  };

  return (
    <div className="relative">
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Header */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span 
              className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-6"
              style={{ animation: 'fadeUp 0.8s 0.1s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              Client Testimonials
            </span>
            <h1 
              className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6"
              style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
            >
              What People{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">Say</span>
            </h1>
            <p 
              className="text-white/50 text-xl leading-relaxed"
              style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              Don't just take my word for it. Here's what clients and colleagues have to say about working with me.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div 
            className="glass-card p-8 lg:p-16 relative overflow-hidden"
            style={{ animation: 'fadeUp 1s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#00ff88]/10 to-transparent rounded-full blur-3xl" />
            <Quote className="absolute top-8 right-8 w-20 h-20 text-white/5" />

            <div className="relative">
              {testimonialsData.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={`transition-all duration-700 ${
                    index === currentIndex
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-8 pointer-events-none'
                  }`}
                >
                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-8">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={24} className="fill-[#00ff88] text-[#00ff88]" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-2xl lg:text-4xl font-medium text-white leading-tight mb-10">
                    "{testimonial.quote}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-5">
                    <div className="relative">
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#00ff88] to-[#7c3aed] rounded-full blur opacity-50" />
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.author}
                        className="relative w-16 h-16 rounded-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">{testimonial.author}</p>
                      <p className="text-white/40">
                        {testimonial.role} at {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3">
                {testimonialsData.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentIndex
                        ? 'w-10 h-3 bg-[#00ff88]'
                        : 'w-3 h-3 bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={goToPrev}
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#00ff88] hover:text-black transition-all duration-300"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={goToNext}
                  className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-[#00ff88] hover:text-black transition-all duration-300"
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="reveal-on-scroll stagger-children text-3xl font-bold text-white mb-16 text-center">
            More Client Reviews
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonialsData.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="reveal-on-scroll stagger-children group glass-card p-8 hover:-translate-y-2 transition-all duration-500"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#00ff88] text-[#00ff88]" />
                  ))}
                </div>

                <p className="text-white/80 text-lg leading-relaxed mb-8 line-clamp-4">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10"
                  />
                  <div>
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-white/40 text-sm">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal-on-scroll stagger-children glass-card p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-[#7c3aed]/20 to-transparent rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <Sparkles className="w-12 h-12 text-[#00ff88] mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Join the List?
              </h2>
              <p className="text-white/50 text-lg mb-8">
                Let's create something amazing together.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <button className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      Let's Talk
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <Link to="/work">
                  <button className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-full font-semibold hover:border-[#00ff88] hover:text-[#00ff88] transition-all duration-300">
                    View My Work
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
